import {
  Category,
  CategoryQuantity,
  DynamicMaterialList,
  Option,
  OptionsListFull,
  PlanPage,
  PlanPageShapeCore,
  SubCategory,
  Takeoff,
  TDynamicVariable,
  TMaterialOutput,
  TValidator,
  MaterialType,
} from "@shared/types";

const DYNAMIC_VARIABLE_INNER = `
  _id
  title
  variableType
  takeoffVariable
  number
  text
  unit
  operator
  categoryId
  subcategoryId
  subcategoryValueId
  optionId
  subtype
  output
`;
export type TDynamicVariableInner = Pick<
  TDynamicVariable,
  | "_id"
  | "title"
  | "variableType"
  | "takeoffVariable"
  | "number"
  | "unit"
  | "operator"
  | "categoryId"
  | "subcategoryId"
  | "subcategoryValueId"
  | "subtype"
  | "output"
  | "optionId"
  | "text"
>;

export const TAKEOFF_QUERY = `query UtilsTakeoff($takeoffId: String!) {
    takeoff( id:  $takeoffId ) {
      _id
      name
      takeoffId
      linePostDistanceOnCenter
      cornerPostDistanceOnCenter
      Product {
        Categories {
          _id
          product
          name
          dynamicName
          soldIn
          quantityAdvanced
          eachLengthAdvanced
          parentProductCategory
          CategoryQuantities {
            Quantity {
              ${DYNAMIC_VARIABLE_INNER}
            }
            validators {
              comparator
              Input {
                ${DYNAMIC_VARIABLE_INNER}
              }
              Output {
                ${DYNAMIC_VARIABLE_INNER}
              }
            }
          }
          DynamicName {
            ${DYNAMIC_VARIABLE_INNER}
          }
          quantity
          Quantity {
            ${DYNAMIC_VARIABLE_INNER}
          }
          eachLength
          EachLength {
            ${DYNAMIC_VARIABLE_INNER}
          }
          CategoryQuantities {
            _id
            validators {
              id
              input
              Input {
                ${DYNAMIC_VARIABLE_INNER}
              }
              comparator
              output
              Output {
                ${DYNAMIC_VARIABLE_INNER}
              }
            }
            name
            categoryId
            Quantity {
              _id
              title
              variableType
              number
              text
              operator
              categoryId
              subcategoryId
              subcategoryValueId
              takeoffVariable
              optionId
              subtype
              output
              unit
            }
            quantity
          }
          SubCategorys {
            _id
            category
          }
        }
        DynamicMaterialList {
            _id
            name
            productId
            validators {
              id
              input
              Input {
                ${DYNAMIC_VARIABLE_INNER}
              }
              comparator
              output
              Output {
                ${DYNAMIC_VARIABLE_INNER}
              }
            }
            output {
              id
              inventoryId
              Inventory {
                name
              }
              quantity
              Quantity {
                ${DYNAMIC_VARIABLE_INNER}
              }
              eachLength
              EachLength {
                ${DYNAMIC_VARIABLE_INNER}
              }
            }
          }
      }
      ProductConfiguration {
        Categories {
          _id
          name
          useParentValues
          parentProductCategory
        }
        OptionsList {
          text
          number
          variableType
          unit
          SubCategory {
            _id
            sortOrder
            Options {
              _id
              name
              sortOrder
              text
              number
              variableType
              unit
            }
            category            
          }
          options
        }
      }
    }
  }`;
type QueryedValidators = Pick<
  TValidator,
  "id" | "input" | "comparator" | "output"
> & {
  Input: TDynamicVariableInner[];
  Output: TDynamicVariableInner[];
};
type QueriedTMaterialOutput = Pick<
  TMaterialOutput,
  "id" | "inventoryId" | "quantity" | "eachLength"
> & {
  Quantity: TDynamicVariableInner[];
  EachLength: TDynamicVariableInner[];
  Inventory: {
    name: string;
  };
};
export type QueryedDynamicMaterialList = Pick<
  DynamicMaterialList,
  "_id" | "name" | "productId"
> & {
  validators: QueryedValidators[];
  output: QueriedTMaterialOutput[];
};
export type QueriedCategory = Pick<
  Category,
  | "_id"
  | "product"
  | "name"
  | "dynamicName"
  | "quantity"
  | "eachLength"
  | "quantityAdvanced"
  | "eachLengthAdvanced"
  | "parentProductCategory"
> & {
  soldIn: string;
  DynamicName: TDynamicVariableInner[];
  Quantity: TDynamicVariableInner[];
  EachLength: TDynamicVariableInner[];
  SubCategorys: Array<Pick<SubCategory, "_id" | "category">>;
  CategoryQuantities: CategoryQuantity[];
} & Partial<Pick<Category, "useParentValues">>;
export type TakeoffQueryItem = Pick<
  Takeoff,
  | "_id"
  | "name"
  | "linePostDistanceOnCenter"
  | "cornerPostDistanceOnCenter"
  | "takeoffId"
> & {
  Product: {
    DynamicMaterialList: QueryedDynamicMaterialList[];
    Categories: QueriedCategory[];
  };
  ProductConfiguration: {
    Categories: Array<
      Pick<
        Category,
        "_id" | "name" | "useParentValues" | "parentProductCategory"
      >
    >;
    OptionsList: Array<
      {
        SubCategory: {
          _id: string;
          Options: Array<
            {
              _id: string;
              name: string;
            } & Pick<Option, "text" | "number" | "variableType" | "unit">
          >;
          category: string;
        };
        options: string[];
      } & Pick<OptionsListFull, "text" | "number" | "variableType" | "unit">
    >;
  };
};

export type QueryOptionListItem = {
  SubCategory: {
    _id: string;
    sortOrder?: number;
    Options: Array<
      {
        _id: string;
        name: string;
        sortOrder?: number;
      } & Pick<Option, "text" | "number" | "variableType" | "unit">
    >;
    category: string;
  };
  options: string[];
} & Pick<OptionsListFull, "text" | "number" | "variableType" | "unit">;

export type TakeoffItem = Pick<
  Takeoff,
  | "_id"
  | "name"
  | "linePostDistanceOnCenter"
  | "cornerPostDistanceOnCenter"
  | "takeoffId"
> & {
  Product: {
    DynamicMaterialList: QueryedDynamicMaterialList[];
    Categories: QueriedCategory[];
  };
  ProductConfiguration: {
    Categories: Array<
      Pick<
        Category,
        "_id" | "name" | "useParentValues" | "parentProductCategory"
      >
    >;
    OptionsList: QueryOptionListItem[];
  };
};
export type TakeoffQueryOutput = {
  takeoff: TakeoffItem;
};
export const SHAPE_QUERY = `
  query UtilsPlanPageShapes($takeoffId: String!) {
    planpageshapes(takeoffId: $takeoffId) {
      _id
      type
      name
      takeoffId
      measuredLength
      computedLength

      PlanPage {
        _id
        name
        plan
        source {
          scale
        }
      }
      config {
        start {
          x
          y
          link
        }
        end {
          x
          y
          link
        }
        point {
          x
          y
        }
        posttype
        fencetype
      }
    }
  }
`;

export type QueryShapeItem = Pick<
  PlanPageShapeCore,
  | "_id"
  | "type"
  | "name"
  | "takeoffId"
  | "measuredLength"
  | "computedLength"
  | "config"
> & {
  PlanPage: Pick<PlanPage, "_id" | "name" | "plan"> & {
    source: {
      scale: number;
    };
  };
};

export type QueryShapeOutput = {
  planpageshapes: QueryShapeItem[];
};

export const MATERIALS_QUERY = `
    query Materials($takeoffId: ID!) {
        materials(takeoffId: $takeoffId ) {
          _id,
          name,
          quantity,
          inventoryId
          automatic
          eachLength
        }
      }
  `;

export type MaterialItem = Pick<
  MaterialType,
  "_id" | "name" | "quantity" | "inventoryId" | "automatic" | "eachLength"
>;

export type MaterialOutput = {
  materials: MaterialItem[];
};
